import { useEffect, useMemo, useState } from 'react'
import { Card, Icon, Table } from '../../../gsys-ui'
import haxios from '../../../lib/haxios'
import useApi from '../../../hooks/useApi'
import moment from 'moment'
import { MagnifyingGlassIcon, PencilIcon, TrashIcon, UsersIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { TextInput, Button, ActionIcon, InputLabel, MenuItem, NativeSelect } from '@mantine/core'
import { modals } from '@mantine/modals'
import { errorToast, groupByKey, successToast } from '../../../lib/helpers'
import clsx from 'clsx'
import s from './StaffTable.module.scss'
import BranchDropdown from '../../../components/BranchDropdown'

const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const Calendar = () => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedCell, setSelectedCell] = useState([null, null])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(11)
  const [selectedYear, setSelectedYear] = useState(2024)

  const { data, isLoading, refetch } = useApi({
    url: '/salesassistnew/tma/calendar',
    enabled: !!selectedBranch,
    data: { branch: selectedBranch, month: selectedMonth, year: selectedYear }
  })

  const displayDateStart = useMemo(
    () => moment().day(1).month(selectedMonth).year(selectedYear).startOf('month'),
    [data]
  )
  const displayDateEnd = moment(displayDateStart).endOf('month')
  const dayCount = displayDateEnd.diff(displayDateStart, 'days') + 1

  if (isLoading) return (
    <div className="absolute inset-0 bg-white p-4 space-y-4">
      <div className="h-10 bg-gray-100 animate-pulse" />
      <div className="space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 animate-pulse" />
        ))}
      </div>
    </div>
  )

  const handleSetBranch = (val) => {
    setSelectedBranch(val)
    setSelectedCell([null, null])
  }

  const handleSetMonth = (val) => {
    setSelectedMonth(val)
    setSelectedCell([null, null])
  }

  const handleSetYear = (val) => {
    setSelectedYear(val)
    setSelectedCell([null, null])
  }

  const handleSetSearch = (val) => {
    setSearchValue(val)
    setSelectedCell([null, null])
  }

  const dataFiltered = data.filter((el) =>
    `${el.FName} ${el.SName} ${el.Branch}`.toLowerCase().includes(searchValue.toLowerCase())
  )

  const dataSorted = dataFiltered.sort((a, b) => {
    const branchComparison = a.Branch.localeCompare(b.Branch)
    if (branchComparison !== 0) {
      return branchComparison
    }

    const positionComparison = a.Position.localeCompare(b.Position)
    if (positionComparison !== 0) {
      return positionComparison
    }

    const aName = `${a.FName} ${a.SName}`
    const bName = `${b.FName} ${b.SName}`
    return aName.localeCompare(bName)
  })

  return (
    <div className="absolute inset-0 bg-white">
      <div class="flex flex-col h-full">
        <div className="flex flex-none justify-between items-center p-2 border-b border-b-gray-200">
          <div className="flex items-center space-x-2"></div>
          <div className="flex items-center space-x-2">
            <BranchDropdown branch={selectedBranch} setBranch={handleSetBranch} />
            <NativeSelect
              value={selectedMonth}
              onChange={(e) => handleSetMonth(e.currentTarget.value)}
              data={[
                { value: 0, label: 'January' },
                { value: 1, label: 'February' },
                { value: 2, label: 'March' },
                { value: 3, label: 'April' },
                { value: 4, label: 'May' },
                { value: 5, label: 'June' },
                { value: 6, label: 'July' },
                { value: 7, label: 'August' },
                { value: 8, label: 'September' },
                { value: 9, label: 'October' },
                { value: 10, label: 'November' },
                { value: 11, label: 'December' }
              ]}
            />
            <NativeSelect
              value={selectedYear}
              onChange={(e) => handleSetYear(e.currentTarget.value)}
              data={Array.from(
                { length: 7 }, // 7 years total: current year ± 3 years
                (_, i) => moment().year() - 3 + i
              ).map((num) => ({
                value: num,
                label: num
              }))}
            />
            <TextInput
              value={searchValue}
              onChange={(e) => handleSetSearch(e.target.value)}
              placeholder="Type to search..."
              leftSection={<Icon Comp={MagnifyingGlassIcon} />}
            />
          </div>
        </div>

        <div class="flex overflow-y-hidden flex-1">
          <div className="flex-1 h-full">
            <div
              className={clsx(
                'overflow-y-scroll border-r border-r-gray-200 w-full h-full relative',
                s.tableColumnHover
              )}
            >
              <div className="flex sticky top-0 z-50 font-semibold text-center bg-white border-b-2 border-b-gray-200">
                <div className="flex flex-1 items-center px-2 space-x-1 border-r-2 border-r-gray-200">
                  <Icon Comp={UsersIcon} />
                  <div>Staff</div>
                </div>
                <div class="flex flex-none" style={{ width: `${dayCount * 32}px` }}>
                  {new Array(dayCount).fill('ayylmao').map((_, ind) => {
                    const thisDate = moment(displayDateStart).add(ind, 'days')
                    const isToday = moment().isSame(thisDate, 'day')
                    const dayOfWeek = daysOfWeek[thisDate.format('d')]
                    const isWeekend = dayOfWeek === 'S'

                    return (
                      <div
                        class={clsx(
                          'pt-0.5 w-8 border-r border-r-gray-200 z-10 relative',
                          s.dayCont,
                          s.highlightColOnly,
                          isWeekend && 'bg-gray-100 '
                        )}
                      >
                        <div class="leading-tight">{ind + 1}</div>
                        <div class="text-sm font-normal leading-tight">{dayOfWeek}</div>
                        <div className={clsx('absolute inset-0 pointer-events-none', s.highlightDiv)} />
                        <div
                          className={clsx(
                            'absolute top-[1px] right-[1px] bottom-[1px] left-[1px] pointer-events-none',
                            isToday && 'outline outline-2 outline-yellow-400 z-50'
                          )}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="z-40 select-none">
                {dataSorted.map((row) => (
                  <StaffRow
                    key={row.StaffId}
                    staff={row}
                    dayCount={dayCount}
                    startDate={displayDateStart}
                    selectedDay={selectedCell[0] === row.StaffId ? selectedCell[1] : null}
                    setSelectedDay={(ind) => setSelectedCell([row.StaffId, ind])}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col h-full w-[330px]">
            <div className="flex-1 p-2 space-y-2 text-right">
              {selectedCell[0] && (
                <Sidebar
                  staff={data.filter((s) => s.StaffId === selectedCell[0]).pop()}
                  selectedDay={moment(displayDateStart).day(selectedCell[1])}
                />
              )}
            </div>

            <div className="flex-none p-2 border-t-2 border-t-gray-200">
              <div className="font-semibold">Calendar legend</div>
              <div className="flex">
                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-green-500 border-2 border-green-700">
                      W
                    </div>
                    <div>Shift worked</div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-gray-300 border-2 border-gray-500">
                      D
                    </div>
                    <div>Due for shift</div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-red-500 border-2 border-red-700">
                      A
                    </div>
                    <div>Absence</div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-purple-500 border-2 border-purple-700">
                      S
                    </div>
                    <div>Sick leave</div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-blue-400 border-2 border-blue-600">
                      H
                    </div>
                    <div>Holiday</div>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-gray-100 border-2 border-gray-300"></div>
                    <div>Not due for shift</div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex justify-center items-center mr-1 w-5 h-5 text-sm bg-white border-2 border-gray-200"></div>
                    <div>Shifts unassigned</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const StaffRow = ({ staff, dayCount, startDate, selectedDay, setSelectedDay }) => {
  return (
    <div class={clsx('flex h-full', s.tRow)}>
      <div
        class={clsx(
          'flex-1 px-2 py-1 border-r-2 border-b border-r-gray-200 border-b-gray-200 h-[63px] relative z-40',
          s.dayCont,
          s.highlightRowOnly
        )}
      >
        <div class="text-lg font-bold">
          {staff.FName} {staff.SName}
        </div>
        <div class="">
          {staff.Branch} | {staff.Position}
        </div>
        <div className={clsx('absolute inset-0 pointer-events-none', s.highlightDiv)} />
        <div
          className={clsx(
            'absolute top-[2px] right-[1px] bottom-[1px] left-[2px] pointer-events-none',
            !!selectedDay && ' outline outline-2 outline-blue-500 z-50'
          )}
        />
      </div>

      <div className=" flex-none p-0  h-[63px] relative">
        {new Array(dayCount).fill('ayylmao').map((_, ind) => {
          const thisDate = moment(startDate).day(ind)
          const shift = staff.Shifts
          const todaysShift = shift?.shifts?.[thisDate.isoWeekday()]
          const clock = staff.Clocklog.filter((cl) => moment(cl.ClockInTime).isSame(thisDate, 'day')).pop()

          const sick = staff.Sicks.filter((si) =>
            thisDate.isBetween(
              moment(si.StartDatetime).startOf('day'),
              moment(si.EndDatetime).endOf('day'),
              undefined,
              '[]'
            )
          ).pop()

          const hol = staff.Holidays.filter((si) =>
            thisDate.isBetween(
              moment(si.StartDatetime).startOf('day'),
              moment(si.EndDatetime).endOf('day'),
              undefined,
              '[]'
            )
          ).pop()

          const isAm = sick || hol ? moment(sick?.EndDatetime || hol?.EndDatetime).hour() === 12 : false
          const isPm = sick || hol ? moment(sick?.StartDatetime || hol?.StartDatetime).hour() === 12 : false

          let status = null
          let clockStatus = null

          if (shift) {
            if (todaysShift) {
              status = 'due'
              clockStatus = 'due'
            } else {
              status = 'noshift'
              clockStatus = 'noshift'
            }
          }

          if (clock) {
            status = 'clocked'
            clockStatus = 'clocked'
          } else {
            if (thisDate.isSameOrBefore(moment(), 'day')) {
              if (status === 'due') {
                status = 'absent'
                clockStatus = 'absent'
              }
            }
          }

          if (sick) {
            status = 'sick'
          }

          if (hol) {
            status = 'hol'
          }

          return (
            <div
              key={ind}
              className={clsx(
                'inline-block w-8 h-full border-r border-r-gray-200 border-b border-b-gray-200 relative cursor-pointer',
                s.dayCont
              )}
              onClick={() => setSelectedDay(ind)}
            >
              <div
                className={clsx(
                  'absolute inset-0 flex flex-col items-center justify-center overflow-hidden',
                  status === 'due' && 'bg-gray-300',
                  status === 'noshift' && 'bg-gray-100',
                  status === 'clocked' && 'bg-green-500',
                  status === 'absent' && 'bg-red-500',
                  status === 'sick' && 'bg-purple-500',
                  status === 'hol' && 'bg-blue-400'
                )}
              >
                {isAm && (
                  <div
                    className={clsx(
                      'absolute right-0 bottom-0 w-0 h-0 border-[15px] border-t-transparent border-l-transparent',
                      clockStatus === 'due' && 'border-b-gray-300 border-r-gray-300',
                      clockStatus === 'clocked' && 'border-b-green-500 border-r-green-500',
                      clockStatus === 'absent' && 'border-b-red-500 border-r-red-500',
                      (clockStatus === 'noshift' || !clockStatus) && 'border-b-white border-r-white'
                    )}
                  />
                )}
                {isPm && (
                  <div
                    className={clsx(
                      'absolute top-0 left-0 w-0 h-0 border-[15px]  border-b-transparent border-r-transparent',
                      clockStatus === 'due' && 'border-t-gray-300 border-l-gray-300',
                      clockStatus === 'clocked' && 'border-t-green-500 border-l-green-500',
                      clockStatus === 'absent' && 'border-t-red-500 border-l-red-500',
                      (clockStatus === 'noshift' || !clockStatus) && 'border-t-white border-l-white'
                    )}
                  />
                )}
                <div>
                  {status === 'due' && 'D'}
                  {status === 'noshift' && ''}
                  {status === 'clocked' && 'W'}
                  {status === 'absent' && 'A'}
                  {status === 'sick' && 'S'}
                  {status === 'hol' && 'H'}
                </div>
              </div>
              <div className={clsx('absolute inset-0 pointer-events-none', s.highlightDiv)} />
              <div
                className={clsx(
                  'absolute top-[1px] right-[1px] bottom-[1px] left-[1px] pointer-events-none',
                  selectedDay === ind && ' outline outline-2 outline-blue-500 z-50'
                )}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const Sidebar = ({ staff, selectedDay, handleClose }) => {
  return (
    <>
      <Button onClick={handleClose}>Close</Button>

      <div className="text-right">
        <div className="text-lg font-semibold">
          {staff.FName} {staff.SName}
        </div>
        <div>
          {staff.Branch} | {staff.Position}
        </div>
      </div>

      <div className="text-right">
        <div className="font-semibold">Date</div>
        <div>{selectedDay.format('dddd, MMMM Mo YYYY')}</div>
      </div>

      <div className="text-right">
        <div className="font-semibold">Status</div>
      </div>

      <div className="text-right">
        <div className="font-semibold">Scheduled shift</div>
      </div>

      <div className="text-right">
        <div className="font-semibold">Additional details</div>
      </div>
    </>
  )
}

export default Calendar
